/**
 * Renders email body HTML inside a sandboxed iframe to prevent email styles
 * (e.g. text-decoration on links) from affecting the rest of the app.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

const BASE_STYLES = `
  body, body * {
    color: #1a1a1a !important;
  }
  body {
    background: #fff !important;
    margin: 0;
    padding: 0.5rem;
    font-family: inherit;
    white-space: normal;
  }
  .email-inline-image-placeholder {
    display: inline-block;
    padding: 0.25rem 0.5rem;
    font-size: 0.75rem;
    color: #6b7280;
    background: #f3f4f6;
    border: 1px solid #e5e7eb;
    border-radius: 0.25rem;
    vertical-align: middle;
  }
`;

/** Add target="_blank" rel="noopener noreferrer" to links so they open in a new tab. */
function addLinkTargets(html: string): string {
    return html.replace(/<a\s/gi, '<a target="_blank" rel="noopener noreferrer" ');
}

function buildIframeDocument(html: string): string {
    const bodyContent = addLinkTargets(html);
    return `<!DOCTYPE html><html><head><meta charset="utf-8"><style>${BASE_STYLES}</style></head><body>${bodyContent}</body></html>`;
}

export interface EmailBodyFrameProps {
    /** HTML already processed by extractBodyContent (may include <style> from email). */
    html: string;
    /** Optional CSS class for the wrapper. */
    className?: string;
    /** Optional fixed height in pixels (e.g. for resizable container). */
    height?: number;
}

export function EmailBodyFrame({ html, className, height }: EmailBodyFrameProps) {
    const { t } = useTranslation(["pages:email-settings"]);
    const tEmail = (key: string, opts?: { defaultValue?: string }) =>
        t(key, { ns: "pages:email-settings", ...opts });

    const srcdoc = useMemo(() => (html ? buildIframeDocument(html) : ""), [html]);

    const title = tEmail("emailView.body", { defaultValue: "Corpo dell'email" });

    if (!html) {
        return null;
    }

    const wrapperStyle: React.CSSProperties = height
        ? { height: height, minHeight: 320 }
        : { minHeight: 320 };
    const iframeStyle: React.CSSProperties = height
        ? { height: height, minHeight: "320px" }
        : { maxHeight: "70vh", minHeight: "320px" };

    return (
        <div className={className} style={wrapperStyle}>
            <iframe
                title={title}
                srcDoc={srcdoc}
                sandbox="allow-same-origin allow-popups"
                className="h-full w-full rounded-lg border-0"
                style={iframeStyle}
            />
        </div>
    );
}
